import { ReactNode, createContext, useContext, useRef } from 'react';
import { Toast as PrToast, ToastProps } from 'primereact/toast';

interface Props extends ToastProps {
  opacity: 'opaque' | 'translucent';
  text: string;
  link: string;
  linkPos: 'left' | 'right';
  type: 'success' | 'warning' | 'error';
  duration: number;
}

const opaqueTypeClassnames = {
  success: 'bg-pv-success-success-text',
  warning: 'bg-pv-warning-warning-text',
  error: 'bg-pv-error-error-text',
};

const translucentTypeClassnames = {
  success:
    'bg-pv-success-success-background border-l-4 border-pv-success-success-text',
  warning:
    'bg-pv-warning-warning-background border-l-4 border-pv-warning-warning-text',
  error: 'bg-pv-error-error-background border-l-4 border-pv-error-error-text',
};

interface ToastContextProps {
  trigger: (props: Props) => void;
}

const ToastContext = createContext<ToastContextProps | undefined>(undefined);

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const toastRef = useRef<PrToast>(null);

  const trigger = ({
    opacity = 'opaque',
    text,
    link,
    linkPos = 'right',
    type = 'success',
    duration = 3000,
  }: Props) => {
    toastRef.current?.show({
      content: (
        <div className="flex gap-6 items-center">
          {link && linkPos === 'left' && (
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={link}
              className={`text-sm font-normal h-fit ${
                opacity === 'opaque' ? `text-white` : `text-black`
              }`}
            >
              View&nbsp;Link
            </a>
          )}
          <span
            className={`text-base ${
              opacity === 'opaque' ? `text-white` : `text-black`
            }`}
          >
            {text}
          </span>
          {link && linkPos === 'right' && (
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={link}
              className={`text-sm font-normal h-fit ${
                opacity === 'opaque' ? `text-white` : `text-black`
              }`}
            >
              View&nbsp;Link
            </a>
          )}
        </div>
      ),
      life: duration,
      pt: {
        root: {
          className: `${
            opacity === 'opaque'
              ? opaqueTypeClassnames[type]
              : translucentTypeClassnames[type]
          }`,
        },
        content: {
          className: `flex items-center justify-between gap-4`,
        },
        closeButton: {
          className: `${opacity === 'opaque' ? `text-white` : `text-black`}`,
        },
      },
    });
  };

  return (
    <ToastContext.Provider value={{ trigger }}>
      {children}
      <PrToast ref={toastRef} position="bottom-left" />
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    console.log('ToastContext error');
  }
  return context;
};
